import { useId, useState } from "react";
import type { FindingKind } from "@shared/ocenaTypy.js";
import { odmien } from "../../lib/polski.js";
import {
  DOMYSLNA_LICZBA_WSKAZAN,
  wybierzPierwsze,
  type ZnaleziskoUzytkownika,
} from "../../lib/wynikUzytkownika.js";
import { IconDoPoprawy, IconSukces } from "../icons/index.js";
import { Card } from "../ui/Card.js";
import { StanPusty } from "../ui/stany.js";
import { Znalezisko } from "./Znalezisko.js";

/**
 * Sekcja wskazan wyniku: "Co wyszło najlepiej" (mocne strony) albo "Co warto poprawić" (rzeczy do poprawy).
 *
 * Domyslnie trzy pierwsze wskazania, a "Pokaż wszystkie (N)" odslania reszte. Naglowek ma znak oceny (ikona zlota przy
 * mocnych stronach, szara przy rzeczach do poprawy: decyzja autora metodyki z 29.10.2026), ale tytul to zwykly tekst
 * w <h2> i to on niesie znaczenie (ST-3). Zero wskazan to uczciwy wynik, a nie blad: zamiast pustej karty jest zdanie
 * o tym, ze nic tu nie wskazalismy, a znak jest neutralny, jak pusta sekcja w karcie administratora (brak mocnych stron
 * nie jest zloty, a nic do poprawy nie jest szare). Gdy ocena jest NIEPELNA (jakiegos obszaru nie udalo sie ocenic),
 * pusta sekcja nie twierdzi, ze w rozmowie nic nie ma: mowi tylko o ocenionych obszarach.
 */

const TEKSTY: Readonly<
  Record<FindingKind, { tytul: string; pusto: string; opisPustego: string; jednostki: [string, string, string] }>
> = {
  "mocna-strona": {
    tytul: "Co wyszło najlepiej",
    pusto: "Nie wskazaliśmy tu mocnych stron",
    opisPustego: "W tej rozmowie nie znaleźliśmy fragmentów, które wyróżniałyby się na plus.",
    jednostki: ["mocna strona", "mocne strony", "mocnych stron"],
  },
  "do-poprawy": {
    tytul: "Co warto poprawić",
    pusto: "Nie wskazaliśmy tu nic do poprawy",
    opisPustego: "W tej rozmowie nie znaleźliśmy fragmentów, które warto by poprawić.",
    jednostki: ["uwaga do poprawy", "uwagi do poprawy", "uwag do poprawy"],
  },
};

const TEKSTY_PUSTE_PRZY_NIEPELNEJ_OCENIE: Readonly<Record<FindingKind, { pusto: string; opisPustego: string }>> = {
  "mocna-strona": {
    pusto: "Brak mocnych stron w ocenionych obszarach",
    opisPustego: "Nie wszystkie obszary udało się ocenić, więc to nie jest pełny obraz rozmowy.",
  },
  "do-poprawy": {
    pusto: "Brak uwag w ocenionych obszarach",
    opisPustego: "Nie wszystkie obszary udało się ocenić, więc to nie znaczy, że w pozostałych nic nie ma do poprawy.",
  },
};

export function SekcjaWskazan({
  rodzaj,
  elementy,
  onPokaz,
  niepelna = false,
}: {
  rodzaj: FindingKind;
  elementy: readonly ZnaleziskoUzytkownika[];
  onPokaz: (indeks: number) => void;
  /** Ktoregos obszaru nie udalo sie ocenic: pusta sekcja nie twierdzi, ze nic nie znaleziono. */
  niepelna?: boolean;
}): React.JSX.Element {
  const [wszystkie, setWszystkie] = useState(false);
  const idNaglowka = useId();
  const idListy = useId();
  const teksty = niepelna && elementy.length === 0 ? { ...TEKSTY[rodzaj], ...TEKSTY_PUSTE_PRZY_NIEPELNEJ_OCENIE[rodzaj] } : TEKSTY[rodzaj];
  const wybor = wybierzPierwsze(elementy, DOMYSLNA_LICZBA_WSKAZAN, wszystkie);
  const mocna = rodzaj === "mocna-strona";
  // Ksztalt ikony mowi, ktora to sekcja, a kolor niesie ocene: przy zerze wskazan nie ma czego oceniac, wiec znak jest
  // neutralny.
  const ton = elementy.length === 0 ? "neutralny" : mocna ? "zloto" : "szarosc";
  // Po "N z M" zawsze dopelniacz liczby mnogiej ("3 z 4 mocnych stron"); po samej liczbie odmiana przez liczebnik.
  const jednostkaPoLiczbie = odmien(wybor.razem, ...teksty.jednostki);
  const jednostkaPoZ = teksty.jednostki[2];

  return (
    <section aria-labelledby={idNaglowka} data-rodzaj={rodzaj}>
      <h2 className="wynik-sekcja" id={idNaglowka}>
        <span className="wynik-sekcja__znak" data-ton={ton} aria-hidden="true">
          {mocna ? <IconSukces size={20} /> : <IconDoPoprawy size={20} />}
        </span>
        {teksty.tytul}
      </h2>

      {elementy.length === 0 ? (
        <Card>
          <StanPusty tytul={teksty.pusto} opis={teksty.opisPustego} />
        </Card>
      ) : (
        <>
          <Card className="card znalezisko-karta">
            <ul className="znalezisko-lista" id={idListy}>
              {wybor.widoczne.map((e) => (
                <li key={e.wskazanie.id}>
                  <Znalezisko element={e} onPokaz={onPokaz} />
                </li>
              ))}
            </ul>
          </Card>

          {/* Przy trzech i mniej wskazaniach nie ma co rozwijac: nie ma przycisku ani licznika. */}
          {wybor.razem > DOMYSLNA_LICZBA_WSKAZAN && (
            <p className="wynik-wiecej">
              <span role="status">
                {wszystkie
                  ? `Pokazujemy wszystkie: ${wybor.razem} ${jednostkaPoLiczbie}.`
                  : `Pokazujemy ${wybor.widoczne.length} z ${wybor.razem} ${jednostkaPoZ}.`}
              </span>
              <button
                type="button"
                className="wynik-przycisk-tekst"
                aria-expanded={wszystkie}
                aria-controls={idListy}
                onClick={() => setWszystkie((w) => !w)}
              >
                {wszystkie ? "Pokaż mniej" : `Pokaż wszystkie (${wybor.razem})`}
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}
